import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HeatmapModule from 'highcharts/modules/heatmap';
import HighchartsMore from 'highcharts/highcharts-more';
import { Search as SearchIcon } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { Filters } from './Filters';
import Loading from '../../components/Loading';
import { SegmentedControl } from '../../components/ui';
import ScreenStatePanel from '../../components/Insights/ScreenStatePanel';
import EntitySelectionModal from '../../components/Reports/EntitySelectionModal';
import useRetentionScreenState from '../../hooks/useRetentionScreenState';
import {
  fetchData,
  formatNumber,
  getUniqueTooltipPoints,
  getSuggestedPeriodByDataRecency
} from '../../utils';
import { sendRequest } from '../../utils/dateHelpers';
import { getValidDevFeedCursor } from '../../utils/devFeedCursor';
import { sendAnalyticsSessionLog } from '../../utils/analytics';
import {
  getAverageDeveloperCostFromSource,
  parseNumericInput
} from '../../utils/companyConfigurations';
import {
  getCohortCellTheme,
  getHeatmapDataLabelColor,
  getRetentionHeatmapStops
} from '../../utils/heatmapTheme';
import {
  getRetentionScreenOverlayContent,
  RETENTION_SCREEN_STATES
} from '../../constants/retentionScreenState';
import { findOptionByValue } from '../../utils/reportData';

HeatmapModule(Highcharts);
HighchartsMore(Highcharts);

const PERIODS = [
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' }
];
const FEED_RENDER_PAGE_SIZE = 30;
const FEED_REQUEST_PAGE_SIZE = 100;
const PIE_COLORS = [
  '#36A2EB',
  '#4C51BF',
  '#15C76F',
  '#FF6D2D',
  '#5C82B8',
  '#C153E8',
  '#EF4444',
  '#0EA5E9'
];
const COMPARISON_BENCHMARK_OPACITY = 0.5;
const COMPANY_RING_OPACITY = 0.32;
const RADAR_SELECTED_COLOR = 'rgba(29, 78, 216, 0.95)';
const RADAR_OTHERS_COLOR = 'rgba(30, 64, 175, 0.16)';

const getAuthorUid = authorFilter => {
  if (!authorFilter) return '';
  if (Array.isArray(authorFilter)) return authorFilter[0]?.value || '';
  return authorFilter.value || '';
};

const formatDateForApi = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getFixedDateRange = () => {
  const endDate = new Date();
  const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - 12, 1);

  return {
    startDate: formatDateForApi(startDate),
    endDate: formatDateForApi(endDate)
  };
};

const getAverageDeveloperCost = userInfo => {
  const directCost = getAverageDeveloperCostFromSource(userInfo);
  if (directCost > 0) return directCost;
  return getAverageDeveloperCostFromSource(userInfo?.user);
};

const convertEffortToInvestment = (value, averageCost) => {
  const effortValue = parseNumericInput(value);
  return ((Number.isFinite(effortValue) ? effortValue : 0) / 60) * averageCost;
};

const convertNumericValues = (payload, averageCost) => {
  if (Array.isArray(payload)) {
    return payload.map(item => convertNumericValues(item, averageCost));
  }

  if (payload && typeof payload === 'object') {
    return Object.fromEntries(
      Object.entries(payload).map(([key, value]) => [
        key,
        convertNumericValues(value, averageCost)
      ])
    );
  }

  if (typeof payload === 'number' || typeof payload === 'string') {
    const parsedValue = parseNumericInput(payload);

    if (Number.isFinite(parsedValue)) {
      return convertEffortToInvestment(parsedValue, averageCost);
    }
  }

  return payload;
};

const convertTrendResponseToInvestment = (responseData, averageCost) => ({
  ...(responseData || {}),
  groupedDataByPeriod: convertNumericValues(
    responseData?.groupedDataByPeriod,
    averageCost
  ),
  total: convertNumericValues(responseData?.total, averageCost)
});

const createSeriesFromMap = (map = {}) => {
  const dates = Object.keys(map).sort((a, b) => new Date(a) - new Date(b));
  return {
    categories: dates,
    values: dates.map(date => map[date] ?? null)
  };
};

const calculateMean = values => {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const calculateMedian = values => {
  if (!values.length) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }

  return sorted[middle];
};

const createHeatmapData = (
  retention,
  metricKey = 'start_value_ratio',
  { displayMode = 'git_effort', averageDeveloperCost = 0 } = {}
) => {
  const rawRetention = Array.isArray(retention) ? retention : [];
  const baseXCategories = [...new Set(rawRetention.map(point => point.x))].sort(
    (a, b) => a - b
  );
  const yCategories = [...new Set(rawRetention.map(point => point.y))].sort();
  const xOrderMap = baseXCategories.reduce((acc, category, index) => {
    acc[String(category)] = index;
    return acc;
  }, {});
  const cohortStartByCategory = rawRetention.reduce((acc, point) => {
    const yCategory = point?.y;
    const pointXOrder = xOrderMap[String(point?.x)];
    const existingPoint = acc[yCategory];
    const existingXOrder = xOrderMap[String(existingPoint?.x)];

    if (
      !existingPoint ||
      (Number.isInteger(pointXOrder) &&
        (!Number.isInteger(existingXOrder) || pointXOrder < existingXOrder))
    ) {
      acc[yCategory] = point;
    }

    return acc;
  }, {});

  const isInvestment = displayMode === 'investment';
  const firstColumnLabel = isInvestment
    ? 'Estimated Cost Without AI ($)'
    : 'REV';
  const xCategories = [firstColumnLabel, ...baseXCategories];
  const cohortValues = yCategories.map((category, index) => {
    const isDarkMode =
      typeof document !== 'undefined' &&
      document.body.classList.contains('dark');
    const cohortCellTheme = getCohortCellTheme(isDarkMode);
    const cohortStartPoint = cohortStartByCategory?.[category];
    const revValue = parseNumericInput(cohortStartPoint?.rev);
    const normalizedRev = Number.isFinite(revValue) ? revValue : 0;
    const value = isInvestment
      ? Math.round((normalizedRev / 60) * averageDeveloperCost)
      : Math.round(normalizedRev);

    return {
      x: 0,
      y: index,
      value,
      isCohortValue: true,
      color: cohortCellTheme.fill,
      borderColor: cohortCellTheme.border
    };
  });

  const retentionValues = rawRetention.map(point => ({
    x: baseXCategories.indexOf(point.x) + 1,
    y: yCategories.indexOf(point.y),
    value: Math.round(
      point?.[metricKey] ?? point.start_value_ratio ?? point.start_value ?? 0
    ),
    isCohortValue: false
  }));

  return {
    data: [...cohortValues, ...retentionValues],
    xCategories,
    yCategories,
    isInvestment
  };
};

const sumNumericValues = values =>
  (values || []).reduce((sum, value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return sum;
    return sum + value;
  }, 0);

const getPieColor = (item, index) =>
  item?.color || PIE_COLORS[index % PIE_COLORS.length];

const formatPiePercentage = value => {
  if (!Number.isFinite(value)) return '0%';
  return `${Number(value.toFixed(1))}%`;
};

const toTransparentColor = (color, opacity) => {
  if (!color) return color;
  const numericOpacity = Number(opacity);
  const clampedOpacity = Number.isFinite(numericOpacity)
    ? Math.max(0, Math.min(1, numericOpacity))
    : 1;

  try {
    const parsedColor = Highcharts.color(color);
    return parsedColor?.setOpacity(clampedOpacity).get('rgba') || color;
  } catch (error) {
    return color;
  }
};

const buildZoomableChartOptions = (options, zoomType = 'x') => ({
  ...options,
  chart: {
    ...options?.chart,
    zooming: {
      type: zoomType
    },
    panning: {
      enabled: true,
      type: zoomType === 'xy' ? 'xy' : 'x'
    },
    panKey: 'shift'
  }
});

const buildPieColorMap = (...groups) => {
  const colorMap = {};
  let colorIndex = 0;

  groups
    .flat()
    .filter(Boolean)
    .forEach(item => {
      const name = item?.name;
      if (!name || colorMap[name]) return;

      const itemColor =
        item?.color || PIE_COLORS[colorIndex % PIE_COLORS.length];
      colorMap[name] = itemColor;
      colorIndex += 1;
    });

  return colorMap;
};

const buildDistributionFromTotals = (totals, labelResolver, maxItems = 10) =>
  Object.entries(totals || {})
    .map(([key, value]) => ({
      name: labelResolver(key),
      y: value
    }))
    .filter(item => Number(item.y) > 0)
    .sort((a, b) => b.y - a.y)
    .slice(0, maxItems);

const buildCategoryDistribution = (trendData, categoriesMap = {}) => {
  const totalsByCategory = {};

  Object.entries(trendData?.groupedDataByPeriod || {}).forEach(([, values]) => {
    Object.entries(values || {}).forEach(([categoryKey, value]) => {
      if (typeof value !== 'number' || Number.isNaN(value)) return;
      totalsByCategory[categoryKey] =
        Number(totalsByCategory[categoryKey] || 0) + value;
    });
  });

  return Object.entries(totalsByCategory)
    .map(([categoryKey, value]) => ({
      name: categoriesMap?.[categoryKey]?.label || categoryKey,
      y: value
    }))
    .filter(item => Number(item.y) > 0)
    .sort((a, b) => b.y - a.y)
    .slice(0, 8);
};

const buildAiDevDistribution = (
  aiTotals = {},
  developerTotals = {},
  isInvestment = false
) =>
  [
    {
      name: isInvestment ? 'Estimated Cost Avoided' : 'Effort Saved by AI',
      y: sumNumericValues(Object.values(aiTotals || {})),
      color: '#8B5CF6'
    },
    {
      name: isInvestment
        ? 'Estimated Human Cost Required'
        : 'Human Effort Required',
      y: sumNumericValues(Object.values(developerTotals || {})),
      color: '#3B82F6'
    }
  ].filter(item => Number(item.y) > 0);

const EMPTY_PIE_BENCHMARK_DISTRIBUTION = {
  repo: [],
  project: [],
  aiDev: [],
  category: []
};

const renderPieLegend = (items = [], isDarkMode = false) => {
  if (!items.length) return null;

  const total = items.reduce((sum, item) => sum + Number(item?.y || 0), 0);

  return (
    <div className="mt-3 overflow-x-auto">
      <div className="inline-flex min-w-full items-center gap-4 whitespace-nowrap pb-1">
        {items.map((item, index) => {
          const value = Number(item?.y || 0);
          const percent = total > 0 ? (value / total) * 100 : 0;

          return (
            <div
              key={`${item?.name || 'slice'}-${index}`}
              className={`inline-flex items-center gap-2 text-sm ${
                isDarkMode ? 'text-slate-200' : 'text-slate-700'
              }`}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: getPieColor(item, index) }}
              />
              <span>{item?.name || 'Unknown'}</span>
              <span
                className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}
              >
                ({formatPiePercentage(percent)})
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const mapTagToLabel = (tag, tagsMap = {}) => {
  if (!tag) return '';
  const tagOptions = Object.values(tagsMap || {});

  const findLabelByValue = value => {
    if (!value) return '';
    const directLabel = tagsMap?.[value]?.label;
    if (directLabel) return directLabel;

    const matchedTag = tagOptions.find(
      option => option?.value === value || option?.id === value
    );
    return matchedTag?.label || '';
  };

  if (typeof tag === 'object') {
    const tagValue = tag.value || tag.id || tag.uid || '';
    return tag.label || findLabelByValue(tagValue) || tagValue;
  }

  if (typeof tag === 'string') {
    return findLabelByValue(tag) || tag;
  }

  return String(tag);
};

const normalizeTagValue = tag => {
  if (!tag) return '';
  if (typeof tag === 'object') {
    return String(tag.value || tag.id || tag.uid || tag.label || '').trim();
  }
  return String(tag).trim();
};

const getNormalizedTagSet = tags => {
  const rawTags = Array.isArray(tags) ? tags.flat(Infinity) : [];
  return new Set(rawTags.map(normalizeTagValue).filter(Boolean));
};

const getTagLabels = (tags, tagsMap = {}) =>
  [...getNormalizedTagSet(tags)]
    .map(tag => mapTagToLabel(tag, tagsMap))
    .filter(Boolean);

const hasNumericSeriesData = values =>
  (values || []).some(
    value => typeof value === 'number' && !Number.isNaN(value)
  );

const createSharedLineTooltipFormatter = (isDarkMode = false) =>
  function () {
    const points = getUniqueTooltipPoints(this.points);
    if (!points.length) return '';

    const chart = points[0]?.series?.chart;
    const hoveredSeriesName =
      chart?.hoverPoint?.series?.name || chart?.hoverSeries?.name || '';

    points.sort((a, b) => {
      const aHovered = a.series.name === hoveredSeriesName;
      const bHovered = b.series.name === hoveredSeriesName;
      if (aHovered && !bHovered) return -1;
      if (!aHovered && bHovered) return 1;
      return 0;
    });

    const title = this.x ?? '';
    const tooltipBg = isDarkMode ? '#0f172a' : '#ffffff';
    const tooltipBorder = isDarkMode ? '#334155' : '#e2e8f0';
    const tooltipTitle = isDarkMode ? '#94a3b8' : '#64748b';
    const tooltipLabel = isDarkMode ? '#cbd5e1' : '#334155';
    const tooltipValue = isDarkMode ? '#f8fafc' : '#0f172a';
    const tooltipMutedValue = isDarkMode ? '#cbd5e1' : '#475569';
    const rows = points
      .map((point, index) => {
        const isFocused = hoveredSeriesName
          ? point.series.name === hoveredSeriesName
          : index === 0;
        const pointValue =
          typeof point.y === 'number' && !Number.isNaN(point.y)
            ? formatNumber(point.y)
            : '-';

        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:2px;">
          <span style="display:flex;align-items:center;gap:6px;color:${tooltipLabel};">
            <span style="color:${point.color};font-size:11px;">●</span>
            ${point.series.name}
          </span>
          <span style="${
            isFocused
              ? `font-weight:700;color:${tooltipValue};`
              : `font-weight:500;color:${tooltipMutedValue};`
          }">${pointValue}</span>
        </div>`;
      })
      .join('');

    return `<div style="min-width:180px;padding:2px 4px;background:${tooltipBg};border:1px solid ${tooltipBorder};border-radius:10px;">
      <div style="font-size:11px;color:${tooltipTitle};margin-bottom:4px;">${title}</div>
      ${rows}
    </div>`;
  };

const createSharedStackedTooltipFormatter = (isDarkMode = false) =>
  function () {
    const points = getUniqueTooltipPoints(this.points);
    if (!points.length) return '';

    const chart = points[0]?.series?.chart;
    const hoveredSeriesName =
      chart?.hoverPoint?.series?.name || chart?.hoverSeries?.name || '';

    points.sort((a, b) => {
      const aHovered = a.series.name === hoveredSeriesName;
      const bHovered = b.series.name === hoveredSeriesName;
      if (aHovered && !bHovered) return -1;
      if (!aHovered && bHovered) return 1;
      return 0;
    });

    const title = this.x ?? '';
    const tooltipBg = isDarkMode ? '#0f172a' : '#ffffff';
    const tooltipBorder = isDarkMode ? '#334155' : '#e2e8f0';
    const tooltipTitle = isDarkMode ? '#94a3b8' : '#64748b';
    const tooltipLabel = isDarkMode ? '#cbd5e1' : '#334155';
    const tooltipValue = isDarkMode ? '#f8fafc' : '#0f172a';
    const tooltipMutedValue = isDarkMode ? '#cbd5e1' : '#475569';
    const tooltipSecondary = isDarkMode ? '#94a3b8' : '#64748b';
    const rows = points
      .map((point, index) => {
        const isFocused = hoveredSeriesName
          ? point.series.name === hoveredSeriesName
          : index === 0;
        const percentageValue = Number(point?.percentage || 0);
        const roundedPercentage = Number(percentageValue.toFixed(1));
        const pointValue =
          typeof point.y === 'number' && !Number.isNaN(point.y)
            ? formatNumber(point.y)
            : '-';

        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-top:2px;">
          <span style="display:flex;align-items:center;gap:6px;color:${tooltipLabel};">
            <span style="color:${point.color};font-size:11px;">●</span>
            ${point.series.name}
          </span>
          <span style="${
            isFocused
              ? `font-weight:700;color:${tooltipValue};`
              : `font-weight:500;color:${tooltipMutedValue};`
          }">${roundedPercentage}% <span style="font-weight:500;color:${tooltipSecondary};">(${pointValue})</span></span>
        </div>`;
      })
      .join('');

    return `<div style="min-width:190px;padding:2px 4px;background:${tooltipBg};border:1px solid ${tooltipBorder};border-radius:10px;">
      <div style="font-size:11px;color:${tooltipTitle};margin-bottom:4px;">${title}</div>
      ${rows}
    </div>`;
  };

const getSeriesBaseOpacity = series =>
  Number.isFinite(Number(series?.options?.custom?.baseOpacity))
    ? Number(series.options.custom.baseOpacity)
    : 1;

const setSeriesOpacity = (series, opacity = 1) => {
  if (!series) return;
  const normalizedOpacity = Math.max(0, Math.min(1, opacity));

  [
    series.group,
    series.markerGroup,
    series.dataLabelsGroup,
    series.graph,
    series.area,
    series.tracker
  ]
    .filter(Boolean)
    .forEach(target => {
      target.attr({ opacity: normalizedOpacity });
    });
};

const applyComparisonBaseOpacity = chart => {
  if (!chart?.series?.length) return;
  chart.series.forEach(series => {
    const baseOpacity = getSeriesBaseOpacity(series);
    setSeriesOpacity(series, baseOpacity);
  });
};

const setupComparisonLegendHover = chart => {
  if (!chart?.series?.length) return;

  chart.series.forEach(currentSeries => {
    const legendElement = currentSeries?.legendItem?.group?.element;
    if (!legendElement) return;

    legendElement.onmouseenter = () => {
      chart.series.forEach(series => {
        const keepOpaque = Boolean(series?.options?.custom?.keepOpaque);
        const isHovered = series === currentSeries;
        const baseOpacity = getSeriesBaseOpacity(series);
        setSeriesOpacity(series, keepOpaque || isHovered ? 1 : baseOpacity);
      });
    };

    legendElement.onmouseleave = () => {
      applyComparisonBaseOpacity(chart);
    };
  });
};

const alignSeriesValues = (categories, series) => {
  const valueMap = Object.fromEntries(
    (series?.categories || []).map((date, index) => [
      date,
      series.values[index]
    ])
  );

  return categories.map(date => valueMap?.[date] ?? null);
};

const getSettledValue = (result, fallback) => {
  if (result?.status === 'fulfilled') return result.value;
  return fallback;
};

const DeveloperReport = () => {
  const { userInfo = {} } = useSelector(state => state);
  const { screenState: baseRetentionScreenState, screenStateMeta } =
    useRetentionScreenState();
  const fixedDateRange = useMemo(() => getFixedDateRange(), []);
  const [filters, setFilters] = useState(() => ({
    dateRange: fixedDateRange
  }));
  const [activePeriod, setActivePeriod] = useState('month');
  const [aiDevTrendViewMode, setAiDevTrendViewMode] = useState('line');
  const [categoryTrendViewMode, setCategoryTrendViewMode] = useState('line');
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined'
      ? document.body.classList.contains('dark')
      : false
  );

  const [loading, setLoading] = useState(false);
  const [comparisonChart, setComparisonChart] = useState(null);
  const [aiVsDeveloperChart, setAiVsDeveloperChart] = useState(null);
  const [categoryChart, setCategoryChart] = useState(null);
  const [repoDistribution, setRepoDistribution] = useState([]);
  const [projectDistribution, setProjectDistribution] = useState([]);
  const [pieBenchmarkDistributions, setPieBenchmarkDistributions] = useState({
    company: EMPTY_PIE_BENCHMARK_DISTRIBUTION
  });
  const [developerRadarChart, setDeveloperRadarChart] = useState({
    categories: [],
    series: []
  });
  const [retention, setRetention] = useState([]);
  const [feedItems, setFeedItems] = useState([]);
  const [feedError, setFeedError] = useState('');
  const [feedCursor, setFeedCursor] = useState(null);
  const [feedHasMore, setFeedHasMore] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [developerSearchQuery, setDeveloperSearchQuery] = useState('');
  const [isDeveloperSearchFocused, setIsDeveloperSearchFocused] =
    useState(false);
  const [isDeveloperSelectionModalOpen, setIsDeveloperSelectionModalOpen] =
    useState(false);
  const [summaryTotals, setSummaryTotals] = useState({
    effort: null,
    investment: null
  });
  const [reportError, setReportError] = useState('');
  const [rankedAuthors, setRankedAuthors] = useState([]);
  const [selectedBenchmarkKey, setSelectedBenchmarkKey] = useState('company');
  const [fallbackAverageDeveloperCost, setFallbackAverageDeveloperCost] =
    useState(0);
  const feedListRef = useRef(null);
  const developerSearchRef = useRef(null);
  const developerSearchInputRef = useRef(null);
  const feedRequestIdRef = useRef(0);
  const feedHasMoreRef = useRef(true);
  const feedLoadingRef = useRef(false);
  const feedBufferedItemsRef = useRef([]);
  const feedBackendHasMoreRef = useRef(true);
  const scrollRestoreRef = useRef({ shouldRestore: false, y: 0 });
  const periodManualOverrideRef = useRef(false);
  const periodAutoResolvedRef = useRef(false);
  const hasUserSelectedAuthorRef = useRef(false);
  const skipSelectionSyncRef = useRef('');

  const authorsMap = userInfo?.dashboardData?.authors || {};
  const tagsLookup = userInfo?.dashboardData?.tags || {};
  const roleBasedAverageDeveloperCost = getAverageDeveloperCost(userInfo);
  const averageDeveloperCost =
    roleBasedAverageDeveloperCost > 0
      ? roleBasedAverageDeveloperCost
      : fallbackAverageDeveloperCost;
  const authorsList = useMemo(
    () =>
      Object.values(authorsMap).sort((a, b) => a.label.localeCompare(b.label)),
    [authorsMap]
  );

  const selectedAuthorUid = getAuthorUid(filters?.authors);
  const selectedAuthor = selectedAuthorUid
    ? authorsMap[selectedAuthorUid]
    : null;
  const selectedAuthorTagLabels = useMemo(
    () => getTagLabels(selectedAuthor?.tags, tagsLookup),
    [selectedAuthor?.tags, tagsLookup]
  );
  const logDeveloperReportInteraction = useCallback(
    (action, extraPayload = {}) => {
      sendAnalyticsSessionLog({
        eventName: 'developer_report_interaction',
        payload: {
          action,
          path: window.location.pathname,
          selected_author_uid: selectedAuthorUid || null,
          selected_author_label: selectedAuthor?.label || null,
          period: activePeriod,
          ...extraPayload
        }
      });
    },
    [activePeriod, selectedAuthor?.label, selectedAuthorUid]
  );
  const preserveScrollPosition = useCallback(() => {
    if (typeof window === 'undefined') return;
    scrollRestoreRef.current = {
      shouldRestore: true,
      y: window.scrollY
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const updateTheme = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    updateTheme();

    const observer = new MutationObserver(updateTheme);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setRankedAuthors(authorsList);
  }, [authorsList]);

  useEffect(() => {
    if (!authorsList.length || selectedAuthorUid) return;
    setIsDeveloperSelectionModalOpen(true);
  }, [authorsList.length, selectedAuthorUid]);

  useEffect(() => {
    if (!selectedAuthorUid) return;
    setIsDeveloperSelectionModalOpen(false);
  }, [selectedAuthorUid]);

  useEffect(() => {
    periodManualOverrideRef.current = false;
    periodAutoResolvedRef.current = false;
  }, [
    selectedAuthorUid,
    filters?.dateRange?.startDate,
    filters?.dateRange?.endDate
  ]);

  const handleFilters = useCallback(
    nextFilters => {
      const normalizedAuthor = Array.isArray(nextFilters?.authors)
        ? nextFilters.authors[0]
        : nextFilters?.authors;
      const nextAuthor =
        typeof normalizedAuthor === 'undefined'
          ? filters?.authors
          : normalizedAuthor;
      const nextSelected = getAuthorUid(nextAuthor);
      const operationChanged =
        nextFilters?.operation && nextFilters.operation !== filters?.operation;

      if (
        (nextSelected && nextSelected !== selectedAuthorUid) ||
        operationChanged
      ) {
        if (nextSelected && nextSelected !== selectedAuthorUid) {
          hasUserSelectedAuthorRef.current = true;
        }
        preserveScrollPosition();
      }

      setFilters({
        ...filters,
        ...nextFilters,
        authors: nextAuthor
      });
    },
    [filters, preserveScrollPosition, selectedAuthorUid]
  );

  const navigateDeveloper = direction => {
    if (!rankedAuthors.length || !selectedAuthorUid) return;

    const currentIndex = rankedAuthors.findIndex(
      item => item.value === selectedAuthorUid
    );
    if (currentIndex === -1) return;

    const targetIndex =
      direction === 'next'
        ? Math.min(currentIndex + 1, rankedAuthors.length - 1)
        : Math.max(currentIndex - 1, 0);
    const targetAuthor = rankedAuthors[targetIndex];
    const isChanged = targetIndex !== currentIndex;

    logDeveloperReportInteraction('developer_navigation_click', {
      direction,
      from_author_uid: selectedAuthorUid,
      to_author_uid: targetAuthor?.value || null,
      to_author_label: targetAuthor?.label || null,
      changed: isChanged
    });

    if (!isChanged) return;

    hasUserSelectedAuthorRef.current = true;
    preserveScrollPosition();
    setFilters(prev => ({
      ...prev,
      authors: rankedAuthors[targetIndex]
    }));
  };

  const handlePeriodChange = useCallback(
    nextPeriod => {
      logDeveloperReportInteraction('period_toggle_click', {
        next_period: nextPeriod
      });

      if (nextPeriod === activePeriod) return;
      preserveScrollPosition();
      periodManualOverrideRef.current = true;
      periodAutoResolvedRef.current = true;
      setActivePeriod(nextPeriod);
    },
    [activePeriod, logDeveloperReportInteraction, preserveScrollPosition]
  );

  const developerSearchOptions = useMemo(
    () =>
      (rankedAuthors.length ? rankedAuthors : authorsList).map(option => {
        const author = authorsMap?.[option?.value] || option;
        const tagLabels = getTagLabels(author?.tags, tagsLookup);

        return {
          ...option,
          badges: tagLabels,
          searchText: [option?.label || '', ...tagLabels].join(' ')
        };
      }),
    [authorsList, authorsMap, rankedAuthors, tagsLookup]
  );

  const selectedDeveloperOption = useMemo(() => {
    return (
      developerSearchOptions.find(
        option => option.value === selectedAuthorUid
      ) || null
    );
  }, [developerSearchOptions, selectedAuthorUid]);

  const handleDeveloperSearchSelect = useCallback(
    option => {
      if (!option?.value || option.value === selectedAuthorUid) return;
      logDeveloperReportInteraction('developer_search_select', {
        selected_author_uid: option.value,
        selected_author_label: option.label || null,
        search_query: developerSearchQuery.trim() || null
      });
      hasUserSelectedAuthorRef.current = true;
      preserveScrollPosition();
      setFilters(prev => ({
        ...prev,
        authors: option
      }));
      setDeveloperSearchQuery(option.label || '');
      setIsDeveloperSearchFocused(false);
      setIsDeveloperSelectionModalOpen(false);
    },
    [
      developerSearchQuery,
      logDeveloperReportInteraction,
      preserveScrollPosition,
      selectedAuthorUid
    ]
  );

  const handleDeveloperSearchFocus = useCallback(() => {
    if (!isDeveloperSearchFocused) {
      logDeveloperReportInteraction('developer_search_focus');
    }
    setIsDeveloperSearchFocused(true);
  }, [isDeveloperSearchFocused, logDeveloperReportInteraction]);

  useEffect(() => {
    if (!isDeveloperSearchFocused) return;

    const handleOutsideClick = event => {
      if (
        developerSearchRef.current &&
        !developerSearchRef.current.contains(event.target)
      ) {
        setDeveloperSearchQuery(selectedDeveloperOption?.label || '');
        setIsDeveloperSearchFocused(false);
      }
    };

    const handleEscape = event => {
      if (event.key === 'Escape') {
        setDeveloperSearchQuery(selectedDeveloperOption?.label || '');
        setIsDeveloperSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDeveloperSearchFocused, selectedDeveloperOption?.label]);

  useEffect(() => {
    if (isDeveloperSearchFocused) return;
    setDeveloperSearchQuery(selectedDeveloperOption?.label || '');
  }, [isDeveloperSearchFocused, selectedDeveloperOption?.label]);

  const filteredDeveloperSearchOptions = useMemo(() => {
    const query = developerSearchQuery.trim().toLowerCase();
    if (!query) return developerSearchOptions.slice(0, 30);

    return developerSearchOptions
      .filter(option =>
        String(option?.searchText || option?.label || '')
          .toLowerCase()
          .includes(query)
      )
      .slice(0, 30);
  }, [developerSearchOptions, developerSearchQuery]);

  useEffect(() => {
    const loadReport = async () => {
      if (
        skipSelectionSyncRef.current &&
        skipSelectionSyncRef.current === selectedAuthorUid
      ) {
        skipSelectionSyncRef.current = '';
        return;
      }

      if (!filters?.dateRange || !authorsList.length || !selectedAuthorUid) {
        return;
      }

      try {
        setLoading(true);
        setReportError('');
        setSummaryTotals({
          effort: null,
          investment: null
        });

        const response = await sendRequest('/dashboard/report-data', 'POST', {
          reportType: 'developer',
          filters,
          period: activePeriod
        });
        const reportMeta = response?.data?.meta || {};
        const reportDatasets = response?.data?.datasets || {};
        const responseAverageDeveloperCost = parseNumericInput(
          reportMeta?.averageDeveloperCost
        );
        const resolvedAverageDeveloperCost =
          roleBasedAverageDeveloperCost > 0
            ? roleBasedAverageDeveloperCost
            : Number.isFinite(responseAverageDeveloperCost)
              ? responseAverageDeveloperCost
              : 0;
        const shouldConvertInvestment =
          filters?.operation === 'investment' &&
          resolvedAverageDeveloperCost > 0;
        const requestOperation =
          reportMeta?.requestOperation ||
          (shouldConvertInvestment ? 'git_effort' : filters?.operation);
        const nextRankedAuthors = authorsList;
        const resolvedSelectedAuthorUid =
          reportMeta?.selectedUid || selectedAuthorUid;
        const resolvedSelectedAuthorOption = findOptionByValue(
          authorsList,
          resolvedSelectedAuthorUid
        );
        const resolvedSelectedAuthor =
          (resolvedSelectedAuthorUid
            ? authorsMap?.[resolvedSelectedAuthorUid]
            : null) || resolvedSelectedAuthorOption;

        if (
          roleBasedAverageDeveloperCost <= 0 &&
          Number.isFinite(responseAverageDeveloperCost) &&
          responseAverageDeveloperCost > 0
        ) {
          setFallbackAverageDeveloperCost(previousValue =>
            previousValue === responseAverageDeveloperCost
              ? previousValue
              : responseAverageDeveloperCost
          );
        }

        setRankedAuthors(nextRankedAuthors);

        if (
          resolvedSelectedAuthorOption &&
          resolvedSelectedAuthorUid &&
          resolvedSelectedAuthorUid !== selectedAuthorUid
        ) {
          skipSelectionSyncRef.current = resolvedSelectedAuthorUid;
          setFilters(prev => ({
            ...prev,
            authors: resolvedSelectedAuthorOption
          }));
        }

        const categoryKeys = Object.keys(
          userInfo?.dashboardData?.categories || {}
        );
        const selectedTrendRaw = reportDatasets?.selectedTrend || {};
        if (
          !periodManualOverrideRef.current &&
          !periodAutoResolvedRef.current
        ) {
          const suggestedPeriod = getSuggestedPeriodByDataRecency(
            Object.keys(selectedTrendRaw?.groupedDataByPeriod || {})
          );
          periodAutoResolvedRef.current = true;

          if (suggestedPeriod !== activePeriod) {
            setActivePeriod(suggestedPeriod);
          }
        }
        const selectedEffortTrendRaw =
          reportDatasets?.selectedEffortTrend || {};
        const selectedInvestmentTrendRaw =
          reportDatasets?.selectedInvestmentTrend || {};
        const companyTrendRaw = reportDatasets?.companyTrend || {};
        const categoryTrendRaw = reportDatasets?.categoryTrend || {};
        const categoryCompanyTrendRaw =
          reportDatasets?.categoryCompanyTrend || {};
        const repoTrendRaw = reportDatasets?.repoTrend || {};
        const repoCompanyTrendRaw = reportDatasets?.repoCompanyTrend || {};
        const projectTrendRaw = reportDatasets?.projectTrend || {};
        const projectCompanyTrendRaw =
          reportDatasets?.projectCompanyTrend || {};
        const retentionData = reportDatasets?.retention || [];
        const aiTrendRaw = reportDatasets?.aiTrend || {};
        const aiCompanyTrendRaw = reportDatasets?.aiCompanyTrend || {};
        const developerTrendRaw = reportDatasets?.developerTrend || {};
        const developerCompanyTrendRaw =
          reportDatasets?.developerCompanyTrend || {};
        const developerBreakdownRaw = reportDatasets?.developerBreakdown || {};
        const selectedTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              selectedTrendRaw,
              resolvedAverageDeveloperCost
            )
          : selectedTrendRaw;
        const selectedEffortTotalRaw = parseNumericInput(
          selectedEffortTrendRaw?.total?.[resolvedSelectedAuthorUid]
        );
        const selectedEffortTotal = Number.isFinite(selectedEffortTotalRaw)
          ? selectedEffortTotalRaw
          : null;
        const investmentFromRate =
          selectedEffortTotal !== null && resolvedAverageDeveloperCost > 0
            ? convertEffortToInvestment(
                selectedEffortTotal,
                resolvedAverageDeveloperCost
              )
            : null;
        const selectedInvestmentTotalRaw = parseNumericInput(
          selectedInvestmentTrendRaw?.total?.[resolvedSelectedAuthorUid]
        );
        const selectedInvestmentTotal = Number.isFinite(
          selectedInvestmentTotalRaw
        )
          ? selectedInvestmentTotalRaw
          : null;

        setSummaryTotals({
          effort: selectedEffortTotal,
          investment:
            investmentFromRate !== null
              ? investmentFromRate
              : selectedInvestmentTotal
        });
        const companyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              companyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : companyTrendRaw;
        const categoryTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              categoryTrendRaw,
              resolvedAverageDeveloperCost
            )
          : categoryTrendRaw;
        const categoryCompanyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              categoryCompanyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : categoryCompanyTrendRaw;
        const repoTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              repoTrendRaw,
              resolvedAverageDeveloperCost
            )
          : repoTrendRaw;
        const repoCompanyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              repoCompanyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : repoCompanyTrendRaw;
        const projectTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              projectTrendRaw,
              resolvedAverageDeveloperCost
            )
          : projectTrendRaw;
        const projectCompanyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              projectCompanyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : projectCompanyTrendRaw;
        const aiTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              aiTrendRaw,
              resolvedAverageDeveloperCost
            )
          : aiTrendRaw;
        const aiCompanyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              aiCompanyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : aiCompanyTrendRaw;
        const developerTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              developerTrendRaw,
              resolvedAverageDeveloperCost
            )
          : developerTrendRaw;
        const developerCompanyTrend = shouldConvertInvestment
          ? convertTrendResponseToInvestment(
              developerCompanyTrendRaw,
              resolvedAverageDeveloperCost
            )
          : developerCompanyTrendRaw;
        const developerBreakdown = shouldConvertInvestment
          ? convertNumericValues(
              developerBreakdownRaw,
              resolvedAverageDeveloperCost
            )
          : developerBreakdownRaw;

        const selectedMap = {};
        Object.entries(selectedTrend?.groupedDataByPeriod || {}).forEach(
          ([date, values]) => {
            selectedMap[date] = values?.[resolvedSelectedAuthorUid] ?? null;
          }
        );

        const selectedSeries = createSeriesFromMap(selectedMap);
        const companyMeanMap = {};
        const companyMedianMap = {};

        Object.entries(companyTrend?.groupedDataByPeriod || {}).forEach(
          ([date, values]) => {
            const numericValues = Object.values(values || {}).filter(
              value => typeof value === 'number' && !Number.isNaN(value)
            );

            companyMeanMap[date] = numericValues.length
              ? Math.round(calculateMean(numericValues))
              : null;
            companyMedianMap[date] = numericValues.length
              ? calculateMedian(numericValues)
              : null;
          }
        );

        const companyMeanSeries = createSeriesFromMap(companyMeanMap);
        const companyMedianSeries = createSeriesFromMap(companyMedianMap);
        const selectedTagKeys = [
          ...getNormalizedTagSet(resolvedSelectedAuthor?.tags)
        ];
        const authorTagSetByUid = Object.fromEntries(
          Object.entries(authorsMap || {}).map(([authorUid, author]) => [
            authorUid,
            getNormalizedTagSet(author?.tags)
          ])
        );
        const tagsLookup = userInfo?.dashboardData?.tags || {};
        const benchmarkSeriesMap = {
          company: [
            {
              name: 'Company Mean',
              sourceSeries: companyMeanSeries,
              color: '#1E40AF',
              lineWidth: 2,
              dashStyle: 'ShortDash',
              baseOpacity: COMPARISON_BENCHMARK_OPACITY
            },
            {
              name: 'Company Median',
              sourceSeries: companyMedianSeries,
              color: '#0F172A',
              lineWidth: 2,
              dashStyle: 'Dot',
              baseOpacity: COMPARISON_BENCHMARK_OPACITY
            }
          ]
        };
        const benchmarkOptions = [{ key: 'company', label: 'Company' }];
        const benchmarkCategories = [
          ...companyMeanSeries.categories,
          ...companyMedianSeries.categories
        ];

        selectedTagKeys.forEach((tagKey, tagIndex) => {
          const tagMeanMap = {};
          const tagMedianMap = {};

          Object.entries(companyTrend?.groupedDataByPeriod || {}).forEach(
            ([date, values]) => {
              const taggedValues = Object.entries(values || {})
                .filter(([authorUid, value]) => {
                  if (typeof value !== 'number' || Number.isNaN(value)) {
                    return false;
                  }
                  return authorTagSetByUid?.[authorUid]?.has(tagKey);
                })
                .map(([, value]) => value);

              tagMeanMap[date] = taggedValues.length
                ? Math.round(calculateMean(taggedValues))
                : null;
              tagMedianMap[date] = taggedValues.length
                ? calculateMedian(taggedValues)
                : null;
            }
          );

          const tagMeanSeries = createSeriesFromMap(tagMeanMap);
          const tagMedianSeries = createSeriesFromMap(tagMedianMap);
          const tagLabel = mapTagToLabel(tagKey, tagsLookup) || tagKey;
          const tagHue = (tagIndex * 59 + 220) % 360;
          const benchmarkKey = `tag:${tagKey}`;
          const tagSeries = [];

          if (hasNumericSeriesData(tagMeanSeries.values)) {
            tagSeries.push({
              name: `#${tagLabel} Mean`,
              sourceSeries: tagMeanSeries,
              color: `hsl(${tagHue}, 72%, 42%)`,
              lineWidth: 1.5,
              dashStyle: 'ShortDash',
              baseOpacity: COMPARISON_BENCHMARK_OPACITY
            });
            benchmarkCategories.push(...tagMeanSeries.categories);
          }

          if (hasNumericSeriesData(tagMedianSeries.values)) {
            tagSeries.push({
              name: `#${tagLabel} Median`,
              sourceSeries: tagMedianSeries,
              color: `hsl(${tagHue}, 72%, 36%)`,
              lineWidth: 1.5,
              dashStyle: 'Dot',
              baseOpacity: COMPARISON_BENCHMARK_OPACITY
            });
            benchmarkCategories.push(...tagMedianSeries.categories);
          }

          if (tagSeries.length) {
            benchmarkSeriesMap[benchmarkKey] = tagSeries;
            benchmarkOptions.push({
              key: benchmarkKey,
              label: `#${tagLabel}`
            });
          }
        });

        const comparisonCategories = [
          ...new Set([...selectedSeries.categories, ...benchmarkCategories])
        ].sort((a, b) => new Date(a) - new Date(b));

        setComparisonChart({
          categories: comparisonCategories,
          developerSeries: {
            name: resolvedSelectedAuthor?.label || 'Selected Developer',
            sourceSeries: selectedSeries,
            color: '#1D4ED8',
            lineWidth: 3,
            baseOpacity: 1,
            keepOpaque: true
          },
          benchmarkSeriesMap,
          benchmarkOptions
        });

        const aiMap = {};
        Object.entries(aiTrend?.groupedDataByPeriod || {}).forEach(
          ([date, values]) => {
            aiMap[date] = values?.[resolvedSelectedAuthorUid] ?? null;
          }
        );
        const developerMap = {};
        Object.entries(developerTrend?.groupedDataByPeriod || {}).forEach(
          ([date, values]) => {
            developerMap[date] = values?.[resolvedSelectedAuthorUid] ?? null;
          }
        );

        const aiSeries = createSeriesFromMap(aiMap);
        const developerSeries = createSeriesFromMap(developerMap);
        const aiVsDeveloperCategories = [
          ...new Set([...aiSeries.categories, ...developerSeries.categories])
        ].sort((a, b) => new Date(a) - new Date(b));

        setAiVsDeveloperChart({
          categories: aiVsDeveloperCategories,
          series: [
            {
              name: shouldConvertInvestment
                ? 'Estimated Cost Avoided'
                : 'Effort Saved by AI',
              data: alignSeriesValues(aiVsDeveloperCategories, aiSeries),
              type: 'spline'
            },
            {
              name: shouldConvertInvestment
                ? 'Estimated Human Cost Required'
                : 'Human Effort Required',
              data: alignSeriesValues(aiVsDeveloperCategories, developerSeries),
              type: 'spline'
            }
          ]
        });

        const categorySeries = categoryKeys
          .map(key => ({
            name: userInfo?.dashboardData?.categories?.[key]?.label || key,
            data: Object.keys(categoryTrend?.groupedDataByPeriod || {}).map(
              date => {
                const value = categoryTrend?.groupedDataByPeriod?.[date]?.[key];
                return typeof value === 'number' ? value : null;
              }
            )
          }))
          .filter(item => item.data.some(point => point !== null));

        setCategoryChart({
          categories: Object.keys(categoryTrend?.groupedDataByPeriod || {}),
          series: categorySeries
        });

        const radarSeries = Object.entries(developerBreakdown || {})
          .map(([authorUid, values]) => {
            const isSelected = authorUid === resolvedSelectedAuthorUid;
            const data = categoryKeys.map(key => Number(values?.[key] || 0));

            return {
              type: 'spline',
              name:
                userInfo?.dashboardData?.authors?.[authorUid]?.label ||
                authorUid,
              data,
              pointPlacement: 'on',
              color: isSelected ? RADAR_SELECTED_COLOR : RADAR_OTHERS_COLOR,
              lineWidth: isSelected ? 3 : 1.5,
              marker: {
                enabled: isSelected,
                radius: 2
              },
              enableMouseTracking: isSelected,
              showInLegend: false,
              custom: {
                isSelected
              }
            };
          })
          .filter(item => item.data.some(point => point > 0))
          .sort(
            (a, b) =>
              Number(b.custom?.isSelected) - Number(a.custom?.isSelected)
          );

        const radarLegendSeries = [
          {
            type: 'spline',
            name: resolvedSelectedAuthor?.label || 'Selected Developer',
            data: categoryKeys.map(() => null),
            pointPlacement: 'on',
            color: RADAR_SELECTED_COLOR,
            lineWidth: 3,
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: true
          },
          {
            type: 'spline',
            name: 'Others',
            data: categoryKeys.map(() => null),
            pointPlacement: 'on',
            color: RADAR_OTHERS_COLOR,
            lineWidth: 1.5,
            marker: { enabled: false },
            enableMouseTracking: false,
            showInLegend: true
          }
        ];

        setDeveloperRadarChart({
          categories: categoryKeys.map(
            key => userInfo?.dashboardData?.categories?.[key]?.label || key
          ),
          series: [...radarLegendSeries, ...radarSeries]
        });

        const repos = buildDistributionFromTotals(
          repoTrend?.total,
          repoUid =>
            userInfo?.dashboardData?.repos?.[repoUid]?.label || repoUid,
          10
        );
        setRepoDistribution(repos);

        const projects = buildDistributionFromTotals(
          projectTrend?.total,
          projectUid =>
            userInfo?.dashboardData?.projects?.[projectUid]?.label ||
            projectUid,
          10
        );
        setProjectDistribution(projects);

        const createPieDistributionPack = ({
          repoData,
          projectData,
          aiData,
          developerData,
          categoryData
        }) => ({
          repo: buildDistributionFromTotals(
            repoData?.total,
            repoUid =>
              userInfo?.dashboardData?.repos?.[repoUid]?.label || repoUid,
            10
          ),
          project: buildDistributionFromTotals(
            projectData?.total,
            projectUid =>
              userInfo?.dashboardData?.projects?.[projectUid]?.label ||
              projectUid,
            10
          ),
          aiDev: buildAiDevDistribution(
            aiData?.total,
            developerData?.total,
            shouldConvertInvestment
          ),
          category: buildCategoryDistribution(
            categoryData,
            userInfo?.dashboardData?.categories
          )
        });

        const benchmarkDistributionMap = {
          company: createPieDistributionPack({
            repoData: repoCompanyTrend,
            projectData: projectCompanyTrend,
            aiData: aiCompanyTrend,
            developerData: developerCompanyTrend,
            categoryData: categoryCompanyTrend
          })
        };

        const tagDistributionEntries = await Promise.all(
          selectedTagKeys.map(async tagKey => {
            const taggedAuthorUids = Object.entries(authorTagSetByUid || {})
              .filter(([, tagSet]) => tagSet?.has(tagKey))
              .map(([authorUid]) => authorUid);
            const benchmarkKey = `tag:${tagKey}`;

            if (!taggedAuthorUids.length) {
              return [benchmarkKey, EMPTY_PIE_BENCHMARK_DISTRIBUTION];
            }

            const taggedAuthors = taggedAuthorUids.map(
              authorUid =>
                userInfo?.dashboardData?.authors?.[authorUid] || {
                  value: authorUid
                }
            );

            const [
              tagCategoryResult,
              tagRepoResult,
              tagProjectResult,
              tagAiResult,
              tagDeveloperResult
            ] = await Promise.allSettled([
              fetchData(
                {
                  ...filters,
                  operation: requestOperation,
                  category: 'categories',
                  period: activePeriod,
                  authors: taggedAuthors
                },
                null,
                '/dashboard/get-table-data-categories'
              ),
              fetchData(
                {
                  ...filters,
                  operation: requestOperation,
                  category: 'repos',
                  period: activePeriod,
                  authors: taggedAuthors
                },
                null,
                '/dashboard/get-table-data'
              ),
              fetchData(
                {
                  ...filters,
                  operation: requestOperation,
                  category: 'projects',
                  period: activePeriod,
                  authors: taggedAuthors
                },
                null,
                '/dashboard/get-table-data'
              ),
              fetchData(
                {
                  ...filters,
                  operation: requestOperation,
                  category: 'authors',
                  period: activePeriod,
                  viewType: 'ai',
                  authors: taggedAuthors
                },
                null,
                '/dashboard/get-table-data'
              ),
              fetchData(
                {
                  ...filters,
                  operation: requestOperation,
                  category: 'authors',
                  period: activePeriod,
                  viewType: 'developer',
                  authors: taggedAuthors
                },
                null,
                '/dashboard/get-table-data'
              )
            ]);

            const tagCategoryRaw = getSettledValue(tagCategoryResult, {});
            const tagRepoRaw = getSettledValue(tagRepoResult, {});
            const tagProjectRaw = getSettledValue(tagProjectResult, {});
            const tagAiRaw = getSettledValue(tagAiResult, {});
            const tagDeveloperRaw = getSettledValue(tagDeveloperResult, {});
            const tagCategoryData = shouldConvertInvestment
              ? convertTrendResponseToInvestment(
                  tagCategoryRaw,
                  resolvedAverageDeveloperCost
                )
              : tagCategoryRaw;
            const tagRepoData = shouldConvertInvestment
              ? convertTrendResponseToInvestment(
                  tagRepoRaw,
                  resolvedAverageDeveloperCost
                )
              : tagRepoRaw;
            const tagProjectData = shouldConvertInvestment
              ? convertTrendResponseToInvestment(
                  tagProjectRaw,
                  resolvedAverageDeveloperCost
                )
              : tagProjectRaw;
            const tagAiData = shouldConvertInvestment
              ? convertTrendResponseToInvestment(
                  tagAiRaw,
                  resolvedAverageDeveloperCost
                )
              : tagAiRaw;
            const tagDeveloperData = shouldConvertInvestment
              ? convertTrendResponseToInvestment(
                  tagDeveloperRaw,
                  resolvedAverageDeveloperCost
                )
              : tagDeveloperRaw;

            return [
              benchmarkKey,
              createPieDistributionPack({
                repoData: tagRepoData,
                projectData: tagProjectData,
                aiData: tagAiData,
                developerData: tagDeveloperData,
                categoryData: tagCategoryData
              })
            ];
          })
        );

        tagDistributionEntries.forEach(([benchmarkKey, benchmarkValue]) => {
          benchmarkDistributionMap[benchmarkKey] =
            benchmarkValue || EMPTY_PIE_BENCHMARK_DISTRIBUTION;
        });
        setPieBenchmarkDistributions(benchmarkDistributionMap);

        setRetention(retentionData?.[0]?.retention || []);
      } catch (error) {
        setReportError(
          error?.message || 'Developer report data could not be loaded.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [
    activePeriod,
    authorsList,
    filters,
    roleBasedAverageDeveloperCost,
    selectedAuthorUid,
    userInfo?.dashboardData?.authors,
    userInfo?.dashboardData?.categories,
    userInfo?.dashboardData?.repos,
    userInfo?.dashboardData?.projects
  ]);

  useEffect(() => {
    feedHasMoreRef.current = feedHasMore;
  }, [feedHasMore]);

  useEffect(() => {
    if (loading || !scrollRestoreRef.current.shouldRestore) return;

    const targetY = scrollRestoreRef.current.y;
    scrollRestoreRef.current.shouldRestore = false;

    requestAnimationFrame(() => {
      window.scrollTo({
        top: targetY,
        behavior: 'auto'
      });
    });
  }, [loading, selectedAuthorUid]);

  useEffect(() => {
    feedLoadingRef.current = feedLoading;
  }, [feedLoading]);

  const loadFeed = useCallback(
    async ({ append = false, cursor = null } = {}) => {
      if (!selectedAuthorUid || !filters?.dateRange) return;
      if (append && (feedLoadingRef.current || !feedHasMoreRef.current)) return;

      if (append && feedBufferedItemsRef.current.length) {
        const nextChunk = feedBufferedItemsRef.current.slice(
          0,
          FEED_RENDER_PAGE_SIZE
        );
        feedBufferedItemsRef.current = feedBufferedItemsRef.current.slice(
          FEED_RENDER_PAGE_SIZE
        );

        const nextHasMore =
          feedBufferedItemsRef.current.length > 0 ||
          feedBackendHasMoreRef.current;
        setFeedItems(prev => [...prev, ...nextChunk]);
        setFeedHasMore(nextHasMore);
        feedHasMoreRef.current = nextHasMore;
        return;
      }

      const requestId = feedRequestIdRef.current + 1;
      feedRequestIdRef.current = requestId;
      setFeedLoading(true);

      if (!append) setFeedError('');

      try {
        const body = {
          limit: FEED_REQUEST_PAGE_SIZE,
          filters: {
            startDate: filters.dateRange.startDate,
            endDate: filters.dateRange.endDate,
            search: '',
            projects: (filters.projects || []).map(item => item.value),
            repos: (filters.repos || []).map(item => item.value),
            developers: [selectedAuthorUid],
            categories: (filters.categories || []).map(item => item.value)
          }
        };

        const validCursor = getValidDevFeedCursor(cursor);
        if (append && validCursor) {
          body.cursor = validCursor;
        }

        const response = await sendRequest('/dev-feed', 'POST', body);
        if (requestId !== feedRequestIdRef.current) return;

        const nextItems = response?.data || [];
        const pagination = response?.pagination || {};
        const visibleItems = nextItems.slice(0, FEED_RENDER_PAGE_SIZE);

        feedBufferedItemsRef.current = nextItems.slice(FEED_RENDER_PAGE_SIZE);
        feedBackendHasMoreRef.current = Boolean(pagination?.hasMore);

        setFeedItems(prev =>
          append ? [...prev, ...visibleItems] : [...visibleItems]
        );
        setFeedCursor(pagination?.nextCursor || null);
        const nextHasMore =
          feedBufferedItemsRef.current.length > 0 ||
          feedBackendHasMoreRef.current;
        setFeedHasMore(nextHasMore);
        feedHasMoreRef.current = nextHasMore;
      } catch (error) {
        if (requestId !== feedRequestIdRef.current) return;

        const message = error?.message || 'Failed to load dev feed.';
        setFeedError(message);
        if (!append) {
          setFeedItems([]);
          setFeedCursor(null);
          setFeedHasMore(false);
          feedHasMoreRef.current = false;
          feedBufferedItemsRef.current = [];
          feedBackendHasMoreRef.current = false;
        } else if (message.includes('Invalid cursor id')) {
          setFeedHasMore(false);
          feedHasMoreRef.current = false;
        }
      } finally {
        if (requestId === feedRequestIdRef.current) {
          setFeedLoading(false);
        }
      }
    },
    [
      filters?.categories,
      filters?.dateRange,
      filters?.projects,
      filters?.repos,
      selectedAuthorUid
    ]
  );

  useEffect(() => {
    if (!selectedAuthorUid || !filters?.dateRange) return;

    setFeedItems([]);
    setFeedCursor(null);
    setFeedHasMore(true);
    feedHasMoreRef.current = true;
    feedBufferedItemsRef.current = [];
    feedBackendHasMoreRef.current = true;

    loadFeed({ append: false, cursor: null });
  }, [
    selectedAuthorUid,
    filters?.dateRange?.startDate,
    filters?.dateRange?.endDate,
    loadFeed
  ]);

  const retentionMetric =
    filters?.calculationsMethod === 'ratio'
      ? 'start_value_ratio'
      : 'start_value';
  const retentionHeatmap = useMemo(() => {
    if (!retention.length) return null;
    return createHeatmapData(retention, retentionMetric, {
      displayMode: filters?.operation || 'git_effort',
      averageDeveloperCost
    });
  }, [
    averageDeveloperCost,
    filters?.operation,
    isDarkMode,
    retention,
    retentionMetric
  ]);
  const resolvedRetentionScreenState = loading
    ? RETENTION_SCREEN_STATES.READY
    : baseRetentionScreenState !== RETENTION_SCREEN_STATES.READY
      ? baseRetentionScreenState
      : retentionHeatmap
        ? RETENTION_SCREEN_STATES.READY
        : RETENTION_SCREEN_STATES.ANALYSIS_PENDING;
  const retentionOverlayContent = getRetentionScreenOverlayContent(
    resolvedRetentionScreenState,
    screenStateMeta
  );

  const aiDevPieDistribution = useMemo(() => {
    const aiSeries = aiVsDeveloperChart?.series?.[0];
    const developerSeries = aiVsDeveloperChart?.series?.[1];

    const aiTotal = sumNumericValues(aiSeries?.data);
    const developerTotal = sumNumericValues(developerSeries?.data);

    return [
      {
        name: aiSeries?.name || 'Effort Saved by AI',
        y: aiTotal,
        color: '#8B5CF6'
      },
      {
        name: developerSeries?.name || 'Human Effort Required',
        y: developerTotal,
        color: '#3B82F6'
      }
    ].filter(item => Number(item.y) > 0);
  }, [aiVsDeveloperChart]);

  const categoryPieDistribution = useMemo(() => {
    return (categoryChart?.series || [])
      .map(item => ({
        name: item.name,
        y: sumNumericValues(item.data)
      }))
      .filter(item => Number(item.y) > 0)
      .sort((a, b) => b.y - a.y)
      .slice(0, 8);
  }, [categoryChart]);

  const metricLabel =
    filters?.operation === 'investment'
      ? 'Estimated Cost Without AI ($)'
      : 'REV';
  const hasReportSnapshot = [
    comparisonChart?.categories?.length,
    aiVsDeveloperChart?.categories?.length,
    categoryChart?.categories?.length,
    repoDistribution.length,
    projectDistribution.length,
    retention.length,
    developerRadarChart?.series?.length,
    feedItems.length,
    reportError
  ].some(Boolean);
  const comparisonBenchmarkOptions = comparisonChart?.benchmarkOptions || [];
  const chartTextColor = isDarkMode ? '#e2e8f0' : '#334155';
  const chartMutedTextColor = isDarkMode ? '#94a3b8' : '#64748b';
  const chartGridColor = isDarkMode ? '#334155' : '#e2e8f0';
  const lineTooltipFormatter = useMemo(
    () => createSharedLineTooltipFormatter(isDarkMode),
    [isDarkMode]
  );
  const stackedTooltipFormatter = useMemo(
    () => createSharedStackedTooltipFormatter(isDarkMode),
    [isDarkMode]
  );
  const chartLegendOptions = useMemo(
    () => ({
      enabled: true,
      itemStyle: {
        color: chartTextColor,
        fontWeight: '500'
      },
      itemHoverStyle: {
        color: isDarkMode ? '#ffffff' : '#0f172a'
      },
      itemHiddenStyle: {
        color: isDarkMode ? '#64748b' : '#94a3b8'
      }
    }),
    [chartTextColor, isDarkMode]
  );
  const xAxisTheme = useMemo(
    () => ({
      lineColor: chartGridColor,
      tickColor: chartGridColor,
      gridLineColor: chartGridColor,
      labels: {
        style: {
          color: chartMutedTextColor
        }
      }
    }),
    [chartGridColor, chartMutedTextColor]
  );
  const yAxisTheme = useMemo(
    () => ({
      gridLineColor: chartGridColor,
      lineColor: chartGridColor,
      tickColor: chartGridColor,
      labels: {
        style: {
          color: chartMutedTextColor
        }
      },
      title: {
        style: {
          color: chartMutedTextColor
        }
      }
    }),
    [chartGridColor, chartMutedTextColor]
  );

  useEffect(() => {
    if (!comparisonBenchmarkOptions.length) return;
    const isSelectedOptionValid = comparisonBenchmarkOptions.some(
      option => option.key === selectedBenchmarkKey
    );

    if (!isSelectedOptionValid) {
      setSelectedBenchmarkKey(comparisonBenchmarkOptions[0].key);
    }
  }, [comparisonBenchmarkOptions, selectedBenchmarkKey]);

  const selectedBenchmarkOption = useMemo(() => {
    if (!comparisonBenchmarkOptions.length) return null;
    return (
      comparisonBenchmarkOptions.find(
        option => option.key === selectedBenchmarkKey
      ) || comparisonBenchmarkOptions[0]
    );
  }, [comparisonBenchmarkOptions, selectedBenchmarkKey]);

  const activePieBenchmarkDistribution = useMemo(() => {
    const fallbackDistribution =
      pieBenchmarkDistributions?.company || EMPTY_PIE_BENCHMARK_DISTRIBUTION;
    const selectedKey =
      selectedBenchmarkOption?.key || selectedBenchmarkKey || 'company';

    return (
      pieBenchmarkDistributions?.[selectedKey] ||
      fallbackDistribution ||
      EMPTY_PIE_BENCHMARK_DISTRIBUTION
    );
  }, [
    pieBenchmarkDistributions,
    selectedBenchmarkKey,
    selectedBenchmarkOption?.key
  ]);

  const outerPieSeriesLabel =
    selectedBenchmarkOption?.key === 'company'
      ? 'Company overall'
      : `${selectedBenchmarkOption?.label || 'Selected benchmark'} benchmark`;

  const comparisonChartSeries = useMemo(() => {
    if (!comparisonChart?.developerSeries) return [];

    const selectedOptionKey = comparisonChart?.benchmarkSeriesMap?.[
      selectedBenchmarkKey
    ]
      ? selectedBenchmarkKey
      : comparisonBenchmarkOptions[0]?.key;
    const selectedBenchmarkSeries =
      comparisonChart?.benchmarkSeriesMap?.[selectedOptionKey] || [];

    return [
      {
        name: comparisonChart.developerSeries.name,
        data: alignSeriesValues(
          comparisonChart.categories,
          comparisonChart.developerSeries.sourceSeries
        ),
        type: 'spline',
        color: comparisonChart.developerSeries.color,
        lineWidth: comparisonChart.developerSeries.lineWidth,
        marker: { enabled: false },
        custom: {
          baseOpacity: comparisonChart.developerSeries.baseOpacity,
          keepOpaque: comparisonChart.developerSeries.keepOpaque
        }
      },
      ...selectedBenchmarkSeries.map(series => ({
        name: series.name,
        data: alignSeriesValues(
          comparisonChart.categories,
          series.sourceSeries
        ),
        type: 'spline',
        color: series.color,
        lineWidth: series.lineWidth,
        marker: { enabled: false },
        dashStyle: series.dashStyle,
        custom: {
          baseOpacity: series.baseOpacity
        }
      }))
    ];
  }, [comparisonBenchmarkOptions, comparisonChart, selectedBenchmarkKey]);

  const isAiDevStackedTrendMode = aiDevTrendViewMode === 'stacked';
  const isCategoryStackedTrendMode = categoryTrendViewMode === 'stacked';

  const aiDevTrendSeries = useMemo(
    () =>
      (aiVsDeveloperChart?.series || []).map(item => ({
        ...item,
        type: isAiDevStackedTrendMode ? 'area' : 'spline',
        stack: isAiDevStackedTrendMode ? 'aiDev' : undefined,
        data: (item?.data || []).map(value => {
          if (!isAiDevStackedTrendMode) return value;
          return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
        }),
        marker: { enabled: false },
        lineWidth: isAiDevStackedTrendMode ? 1.5 : 2,
        fillOpacity: isAiDevStackedTrendMode ? 0.75 : 0
      })),
    [aiVsDeveloperChart?.series, isAiDevStackedTrendMode]
  );

  const categoryTrendSeries = useMemo(
    () =>
      (categoryChart?.series || []).map(item => ({
        ...item,
        type: isCategoryStackedTrendMode ? 'area' : 'spline',
        stack: isCategoryStackedTrendMode ? 'categories' : undefined,
        data: (item?.data || []).map(value => {
          if (!isCategoryStackedTrendMode) return value;
          return typeof value === 'number' && !Number.isNaN(value) ? value : 0;
        }),
        marker: { enabled: false },
        lineWidth: isCategoryStackedTrendMode ? 1.3 : 2,
        fillOpacity: isCategoryStackedTrendMode ? 0.7 : 0
      })),
    [categoryChart?.series, isCategoryStackedTrendMode]
  );

  const handleComparisonChange = useCallback(
    nextKey => {
      const nextOption = comparisonBenchmarkOptions.find(
        option => option.key === nextKey
      );
      logDeveloperReportInteraction('comparison_toggle_click', {
        comparison_key: nextKey,
        comparison_label: nextOption?.label || null
      });

      if (nextKey === selectedBenchmarkKey) return;
      setSelectedBenchmarkKey(nextKey);
    },
    [
      comparisonBenchmarkOptions,
      logDeveloperReportInteraction,
      selectedBenchmarkKey
    ]
  );

  const handleAiDevTrendViewModeChange = useCallback(
    nextMode => {
      logDeveloperReportInteraction('trend_view_toggle_click', {
        chart_scope: 'ai_vs_developer',
        trend_view_mode: nextMode
      });

      if (nextMode === aiDevTrendViewMode) return;
      setAiDevTrendViewMode(nextMode);
    },
    [aiDevTrendViewMode, logDeveloperReportInteraction]
  );

  const handleCategoryTrendViewModeChange = useCallback(
    nextMode => {
      logDeveloperReportInteraction('trend_view_toggle_click', {
        chart_scope: 'category_trends',
        trend_view_mode: nextMode
      });

      if (nextMode === categoryTrendViewMode) return;
      setCategoryTrendViewMode(nextMode);
    },
    [categoryTrendViewMode, logDeveloperReportInteraction]
  );

  const createPieOptions = (
    seriesData,
    comparisonSeriesData = [],
    comparisonSeriesLabel = 'Company overall'
  ) => {
    const maxPointIndex = seriesData.reduce((bestIndex, item, index, list) => {
      const bestValue = Number(list?.[bestIndex]?.y || 0);
      const currentValue = Number(item?.y || 0);
      return currentValue > bestValue ? index : bestIndex;
    }, 0);
    const colorMap = buildPieColorMap(seriesData, comparisonSeriesData);
    const total = seriesData.reduce(
      (sum, item) => sum + Number(item?.y || 0),
      0
    );
    const normalizedData = seriesData.map((item, index) => ({
      ...item,
      color: colorMap[item?.name] || getPieColor(item, index),
      custom: {
        ...item.custom,
        isTopSlice: index === maxPointIndex,
        percentage: total > 0 ? (Number(item?.y || 0) / total) * 100 : 0
      }
    }));
    const comparisonTotal = comparisonSeriesData.reduce(
      (sum, item) => sum + Number(item?.y || 0),
      0
    );
    const normalizedComparisonData = comparisonSeriesData.map(
      (item, index) => ({
        ...item,
        color: toTransparentColor(
          colorMap[item?.name] || getPieColor(item, index),
          COMPANY_RING_OPACITY
        ),
        custom: {
          ...item.custom,
          percentage:
            comparisonTotal > 0
              ? (Number(item?.y || 0) / comparisonTotal) * 100
              : 0
        }
      })
    );
    const hasComparisonLayer = normalizedComparisonData.length > 0;

    return {
      credits: { enabled: false },
      legend: { enabled: false },
      chart: {
        backgroundColor: 'transparent',
        height: 360,
        spacingTop: 0,
        spacingBottom: 8
      },
      title: { text: '' },
      tooltip: {
        pointFormatter: function () {
          const percent =
            this.point?.custom?.percentage ?? this.percentage ?? 0;
          return `<b>${this.series.name}:</b> ${formatNumber(this.y)} (${formatPiePercentage(percent)})`;
        }
      },
      plotOptions: {
        pie: {
          showInLegend: false,
          size: 240,
          innerSize: '56%',
          dataLabels: {
            enabled: true,
            distance: 26,
            connectorWidth: 1,
            connectorColor: chartGridColor,
            style: {
              color: chartTextColor,
              textOutline: 'none'
            },
            softConnector: true,
            crop: false,
            overflow: 'allow',
            formatter: function () {
              if (!this.point?.custom?.isTopSlice) return null;
              const percent =
                this.point?.custom?.percentage ?? this.percentage ?? 0;
              return `${this.point.name}: ${formatNumber(this.y)} (${formatPiePercentage(percent)})`;
            }
          }
        }
      },
      responsive: {
        rules: [
          {
            condition: {
              maxWidth: 480
            },
            chartOptions: {
              chart: {
                height: 300
              },
              plotOptions: {
                pie: {
                  size: 190,
                  dataLabels: {
                    distance: 14
                  }
                }
              }
            }
          }
        ]
      },
      series: [
        ...(hasComparisonLayer
          ? [
              {
                type: 'pie',
                name: comparisonSeriesLabel,
                size: 250,
                innerSize: '78%',
                dataLabels: { enabled: false },
                data: normalizedComparisonData,
                enableMouseTracking: true
              }
            ]
          : []),
        {
          type: 'pie',
          name: selectedAuthor?.label || 'Selected developer',
          size: hasComparisonLayer ? 200 : 240,
          innerSize: '56%',
          data: normalizedData
        }
      ]
    };
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div
        className={`z-40 rounded-md border px-3 py-2.5 shadow-default sm:px-4 sm:py-3 md:sticky md:top-20 ${
          isDarkMode
            ? 'border-strokedark bg-boxdark/95 backdrop-blur'
            : 'border-stroke bg-white'
        }`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <h2
                className={`text-lg font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {selectedAuthor?.label || 'Select a developer'}
              </h2>
              {!!selectedAuthorTagLabels.length && (
                <span
                  className={`max-w-full truncate text-xs font-medium ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  {selectedAuthorTagLabels
                    .map(tagLabel => `#${tagLabel}`)
                    .join(' ')}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigateDeveloper('prev')}
              disabled={!selectedAuthorUid}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'border-stroke text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setIsDeveloperSelectionModalOpen(true)}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4'
                  : 'border-stroke text-slate-700 hover:bg-slate-50'
              }`}
            >
              {selectedAuthorUid ? 'Change' : 'Select'}
            </button>
            <button
              type="button"
              onClick={() => navigateDeveloper('next')}
              disabled={!selectedAuthorUid}
              className={`rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4 disabled:cursor-not-allowed disabled:opacity-50'
                  : 'border-stroke text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50'
              }`}
            >
              Next →
            </button>
          </div>
        </div>
        <div className="mt-2.5 flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Filters
              userInfo={userInfo}
              onChange={handleFilters}
              isHidden={[
                'projects',
                'repos',
                'authors',
                'categories',
                'tags',
                'language_tags',
                'viewType',
                'clearButton'
              ]}
              operationKeys={['GIT_EFFORT', 'INVESTMENT']}
              showContextSummary={true}
              summaryCategory="developer_report"
            />
            {!!comparisonBenchmarkOptions.length && (
              <div
                className={`flex flex-wrap items-center gap-1.5 rounded-md border px-2 py-1 ${
                  isDarkMode
                    ? 'border-strokedark bg-meta-4'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <span
                  className={`px-1 text-[10px] font-semibold uppercase tracking-wide ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Comparison
                </span>
                <div
                  className={`inline-flex items-center rounded-md p-0.5 shadow-sm ${
                    isDarkMode ? 'bg-boxdark' : 'bg-white'
                  }`}
                >
                  {comparisonBenchmarkOptions.map(option => {
                    const isSelected = selectedBenchmarkKey === option.key;

                    return (
                      <label
                        key={option.key}
                        className={`relative cursor-pointer rounded-md px-2.5 py-1 text-xs font-semibold transition-all ${
                          isSelected
                            ? 'bg-primary text-white shadow-sm'
                            : isDarkMode
                              ? 'text-slate-300 hover:bg-meta-4 hover:text-white'
                              : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800'
                        }`}
                      >
                        <input
                          type="radio"
                          name="benchmark-group-global"
                          value={option.key}
                          checked={isSelected}
                          onChange={() => handleComparisonChange(option.key)}
                          className="sr-only"
                        />
                        <span className="whitespace-nowrap">
                          {option.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            <div
              className="relative w-full sm:w-[360px] xl:w-[420px]"
              ref={developerSearchRef}
            >
              <div
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${
                  isDarkMode
                    ? 'border-strokedark bg-boxdark text-slate-200'
                    : 'border-stroke bg-white text-slate-700'
                }`}
              >
                <SearchIcon
                  className={`!h-4 !w-4 ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                />
                <input
                  ref={developerSearchInputRef}
                  type="text"
                  value={developerSearchQuery}
                  onFocus={handleDeveloperSearchFocus}
                  onChange={event => {
                    setDeveloperSearchQuery(event.target.value);
                    setIsDeveloperSearchFocused(true);
                  }}
                  onKeyDown={event => {
                    if (
                      event.key === 'Enter' &&
                      filteredDeveloperSearchOptions[0]
                    ) {
                      event.preventDefault();
                      handleDeveloperSearchSelect(
                        filteredDeveloperSearchOptions[0]
                      );
                    }
                  }}
                  placeholder="Search developer..."
                  className={`w-full bg-transparent text-sm outline-none ${
                    isDarkMode
                      ? 'text-slate-100 placeholder:text-slate-500'
                      : 'text-slate-700 placeholder:text-slate-400'
                  }`}
                />
                {!!developerSearchQuery && (
                  <button
                    type="button"
                    onMouseDown={event => event.preventDefault()}
                    onClick={() => {
                      setDeveloperSearchQuery('');
                      setIsDeveloperSearchFocused(true);
                      developerSearchInputRef.current?.focus();
                    }}
                    aria-label="Clear search"
                    className={`rounded-sm px-1 text-base leading-none transition-colors ${
                      isDarkMode
                        ? 'text-slate-500 hover:text-slate-200'
                        : 'text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    ×
                  </button>
                )}
              </div>

              {isDeveloperSearchFocused && (
                <div
                  className={`absolute left-0 top-full z-50 mt-2 max-h-64 w-full overflow-auto rounded-md border py-1 shadow-lg ${
                    isDarkMode
                      ? 'border-strokedark bg-boxdark'
                      : 'border-stroke bg-white'
                  }`}
                >
                  {filteredDeveloperSearchOptions.length ? (
                    filteredDeveloperSearchOptions.map(option => {
                      const isSelected = option.value === selectedAuthorUid;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onMouseDown={event => event.preventDefault()}
                          onClick={() => handleDeveloperSearchSelect(option)}
                          className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                            isSelected
                              ? 'bg-primary/10 font-semibold text-primary'
                              : isDarkMode
                                ? 'text-slate-200 hover:bg-meta-4'
                                : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="min-w-0 flex items-center gap-2 overflow-hidden">
                                <span className="truncate">{option.label}</span>
                                {!!option?.badges?.length && (
                                  <span
                                    className={`truncate text-xs font-medium ${
                                      isDarkMode
                                        ? 'text-slate-400'
                                        : 'text-slate-500'
                                    }`}
                                  >
                                    {option.badges
                                      .slice(0, 6)
                                      .map(tagLabel => `#${tagLabel}`)
                                      .join(' ')}
                                  </span>
                                )}
                              </div>
                              {isSelected && (
                                <span className="ml-2 text-xs font-semibold uppercase tracking-wide">
                                  Selected
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <p
                      className={`px-3 py-2 text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      No developers found.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
          <SegmentedControl
            items={PERIODS}
            value={activePeriod}
            onChange={handlePeriodChange}
          />
        </div>
      </div>
      {reportError && (
        <div
          className={`rounded-sm border px-4 py-3 text-sm ${
            isDarkMode
              ? 'border-red-500/30 bg-red-950/40 text-red-200'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
        >
          {reportError}
        </div>
      )}

      {loading && !hasReportSnapshot ? (
        <div
          className={`rounded-sm border p-6 shadow-default ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-stroke bg-white'
          }`}
        >
          <Loading type="spline" />
        </div>
      ) : (
        <>
          {loading && (
            <div
              className={`rounded-sm border px-3 py-2 text-xs font-medium ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4 text-slate-300'
                  : 'border-slate-200 bg-slate-50 text-slate-600'
              }`}
            >
              Updating report...
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div
              className={`flex h-full flex-col rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Baseline Effort (Without AI)
              </p>
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                R.E.V. shows how much contribution a change represents, in terms
                of the time a typical developer would spend.
              </p>
              <p
                className={`mt-auto pt-3 text-2xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {summaryTotals.effort !== null
                  ? formatNumber(summaryTotals.effort)
                  : '-'}
              </p>
            </div>

            <div
              className={`flex h-full flex-col rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <p
                className={`text-xs font-semibold uppercase tracking-wide ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Estimated Cost Without AI
              </p>
              <p
                className={`mt-1 text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                Estimated Cost Without AI shows the financial equivalent of the
                baseline effort required to deliver the same team output without
                AI, based on REV and your average developer cost per hour. It
                represents effort, not profit.
              </p>
              <p
                className={`mt-auto pt-3 text-2xl font-bold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                {summaryTotals.investment !== null
                  ? `$${formatNumber(summaryTotals.investment)}`
                  : '-'}
              </p>
            </div>
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-3 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Developer Performance Trend
            </h3>
            <HighchartsReact
              highcharts={Highcharts}
              options={buildZoomableChartOptions({
                chart: {
                  backgroundColor: 'transparent',
                  events: {
                    load: function () {
                      applyComparisonBaseOpacity(this);
                      setupComparisonLegendHover(this);
                    },
                    redraw: function () {
                      applyComparisonBaseOpacity(this);
                      setupComparisonLegendHover(this);
                    }
                  }
                },
                credits: { enabled: false },
                title: { text: '' },
                legend: chartLegendOptions,
                tooltip: {
                  shared: true,
                  useHTML: true,
                  formatter: lineTooltipFormatter
                },
                plotOptions: {
                  series: {
                    states: {
                      inactive: {
                        opacity: 1
                      }
                    }
                  }
                },
                xAxis: {
                  ...xAxisTheme,
                  categories: comparisonChart?.categories || []
                },
                yAxis: {
                  ...yAxisTheme,
                  title: {
                    ...yAxisTheme.title,
                    text: metricLabel
                  }
                },
                series: comparisonChartSeries
              })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div
              className={`rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  AI Leverage Trend
                </h3>
                <div
                  className={`inline-flex items-center rounded-md border p-0.5 ${
                    isDarkMode
                      ? 'border-strokedark bg-meta-4'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <button
                    type="button"
                    title="Line view"
                    aria-label="Line view"
                    onClick={() => handleAiDevTrendViewModeChange('line')}
                    className={`rounded-md p-1.5 transition-all ${
                      aiDevTrendViewMode === 'line'
                        ? isDarkMode
                          ? 'bg-white/10 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2,14 7,9 11,12 18,5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="100% stacked area view"
                    aria-label="100% stacked area view"
                    onClick={() => handleAiDevTrendViewModeChange('stacked')}
                    className={`rounded-md p-1.5 transition-all ${
                      aiDevTrendViewMode === 'stacked'
                        ? isDarkMode
                          ? 'bg-white/10 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
                      <path
                        d="M2 16L2 12L8 10L14 11L18 9L18 16Z"
                        opacity="0.35"
                      />
                      <path
                        d="M2 12L2 8L8 6L14 7L18 4L18 9L14 11L8 10Z"
                        opacity="0.65"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions({
                  credits: { enabled: false },
                  title: { text: '' },
                  chart: {
                    backgroundColor: 'transparent'
                  },
                  legend: chartLegendOptions,
                  tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: isAiDevStackedTrendMode
                      ? stackedTooltipFormatter
                      : lineTooltipFormatter
                  },
                  xAxis: {
                    ...xAxisTheme,
                    categories: aiVsDeveloperChart?.categories || []
                  },
                  yAxis: {
                    ...yAxisTheme,
                    title: {
                      ...yAxisTheme.title,
                      text: isAiDevStackedTrendMode ? 'Share (%)' : metricLabel
                    },
                    min: isAiDevStackedTrendMode ? 0 : null,
                    max: isAiDevStackedTrendMode ? 100 : null,
                    labels: {
                      formatter: function () {
                        return isAiDevStackedTrendMode
                          ? `${Number(this.value)}%`
                          : this.value;
                      }
                    }
                  },
                  plotOptions: isAiDevStackedTrendMode
                    ? {
                        area: {
                          stacking: 'percent'
                        }
                      }
                    : {},
                  series: aiDevTrendSeries
                })}
              />
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3
                  className={`text-base font-semibold ${
                    isDarkMode ? 'text-white' : 'text-slate-900'
                  }`}
                >
                  Category Trends
                </h3>
                <div
                  className={`inline-flex items-center rounded-md border p-0.5 ${
                    isDarkMode
                      ? 'border-strokedark bg-meta-4'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <button
                    type="button"
                    title="Line view"
                    aria-label="Line view"
                    onClick={() => handleCategoryTrendViewModeChange('line')}
                    className={`rounded-md p-1.5 transition-all ${
                      categoryTrendViewMode === 'line'
                        ? isDarkMode
                          ? 'bg-white/10 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg
                      viewBox="0 0 20 20"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="2,14 7,9 11,12 18,5" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    title="100% stacked area view"
                    aria-label="100% stacked area view"
                    onClick={() => handleCategoryTrendViewModeChange('stacked')}
                    className={`rounded-md p-1.5 transition-all ${
                      categoryTrendViewMode === 'stacked'
                        ? isDarkMode
                          ? 'bg-white/10 text-primary shadow-sm'
                          : 'bg-white text-primary shadow-sm'
                        : isDarkMode
                          ? 'text-slate-400 hover:text-white'
                          : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <svg viewBox="0 0 20 20" className="h-4 w-4 fill-current">
                      <path
                        d="M2 16L2 12L8 10L14 11L18 9L18 16Z"
                        opacity="0.35"
                      />
                      <path
                        d="M2 12L2 8L8 6L14 7L18 4L18 9L14 11L8 10Z"
                        opacity="0.65"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions({
                  credits: { enabled: false },
                  title: { text: '' },
                  chart: {
                    backgroundColor: 'transparent'
                  },
                  legend: chartLegendOptions,
                  tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: isCategoryStackedTrendMode
                      ? stackedTooltipFormatter
                      : lineTooltipFormatter
                  },
                  xAxis: {
                    ...xAxisTheme,
                    categories: categoryChart?.categories || []
                  },
                  yAxis: {
                    ...yAxisTheme,
                    title: {
                      ...yAxisTheme.title,
                      text: isCategoryStackedTrendMode
                        ? 'Share (%)'
                        : metricLabel
                    },
                    min: isCategoryStackedTrendMode ? 0 : null,
                    max: isCategoryStackedTrendMode ? 100 : null,
                    labels: {
                      formatter: function () {
                        return isCategoryStackedTrendMode
                          ? `${Number(this.value)}%`
                          : this.value;
                      }
                    }
                  },
                  plotOptions: isCategoryStackedTrendMode
                    ? {
                        area: {
                          stacking: 'percent'
                        }
                      }
                    : {},
                  series: categoryTrendSeries
                })}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Repo Distribution
              </h3>
              {!!activePieBenchmarkDistribution?.repo?.length && (
                <p
                  className={`mb-2 text-xs ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Inner ring: selected developer, outer ring:{' '}
                  {outerPieSeriesLabel}.
                </p>
              )}
              {repoDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      repoDistribution,
                      activePieBenchmarkDistribution?.repo || [],
                      outerPieSeriesLabel
                    )}
                  />
                  {renderPieLegend(repoDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No repo distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Project Distribution
              </h3>
              {!!activePieBenchmarkDistribution?.project?.length && (
                <p
                  className={`mb-2 text-xs ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Inner ring: selected developer, outer ring:{' '}
                  {outerPieSeriesLabel}.
                </p>
              )}
              {projectDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      projectDistribution,
                      activePieBenchmarkDistribution?.project || [],
                      outerPieSeriesLabel
                    )}
                  />
                  {renderPieLegend(projectDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No project distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                AI / Developer Distribution
              </h3>
              {!!activePieBenchmarkDistribution?.aiDev?.length && (
                <p
                  className={`mb-2 text-xs ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Inner ring: selected developer, outer ring:{' '}
                  {outerPieSeriesLabel}.
                </p>
              )}
              {aiDevPieDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      aiDevPieDistribution,
                      activePieBenchmarkDistribution?.aiDev || [],
                      outerPieSeriesLabel
                    )}
                  />
                  {renderPieLegend(aiDevPieDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No AI/Developer distribution data in selected range.
                </p>
              )}
            </div>

            <div
              className={`rounded-sm border p-4 shadow-default xl:col-span-1 ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark'
                  : 'border-stroke bg-white'
              }`}
            >
              <h3
                className={`mb-3 text-base font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Category Distribution
              </h3>
              {!!activePieBenchmarkDistribution?.category?.length && (
                <p
                  className={`mb-2 text-xs ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  Inner ring: selected developer, outer ring:{' '}
                  {outerPieSeriesLabel}.
                </p>
              )}
              {categoryPieDistribution.length ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={createPieOptions(
                      categoryPieDistribution,
                      activePieBenchmarkDistribution?.category || [],
                      outerPieSeriesLabel
                    )}
                  />
                  {renderPieLegend(categoryPieDistribution, isDarkMode)}
                </>
              ) : (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No category distribution data in selected range.
                </p>
              )}
            </div>
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-1 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Effort Survival Cohort
            </h3>
            <p
              className={`mb-3 text-xs ${
                isDarkMode ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              Cohort-based view of how much past engineering effort remains
              effective over time
            </p>
            {loading && !retentionHeatmap ? (
              <Loading type="retention" />
            ) : retentionOverlayContent ? (
              <ScreenStatePanel {...retentionOverlayContent} />
            ) : retentionHeatmap ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={buildZoomableChartOptions(
                  {
                    chart: {
                      type: 'heatmap',
                      height: 560,
                      backgroundColor: 'transparent'
                    },
                    credits: { enabled: false },
                    title: { text: '' },
                    xAxis: {
                      ...xAxisTheme,
                      categories: retentionHeatmap.xCategories,
                      opposite: true
                    },
                    yAxis: {
                      ...yAxisTheme,
                      categories: retentionHeatmap.yCategories,
                      title: null,
                      reversed: true
                    },
                    colorAxis: {
                      min: 0,
                      max: 100,
                      stops: getRetentionHeatmapStops(isDarkMode)
                    },
                    series: [
                      {
                        type: 'heatmap',
                        data: retentionHeatmap.data,
                        borderWidth: 1,
                        dataLabels: {
                          enabled: true,
                          useHTML: true,
                          style: {
                            textOutline: 'none'
                          },
                          formatter: function () {
                            const roundedValue = Math.round(this.point.value);
                            const formattedValue = formatNumber(roundedValue);
                            const labelColor = getHeatmapDataLabelColor({
                              value: this.point.value,
                              isDarkMode,
                              isCohortValue: this.point.isCohortValue
                            });

                            if (this.point.isCohortValue) {
                              const label = retentionHeatmap.isInvestment
                                ? `$${formattedValue}`
                                : `${formattedValue}`;

                              return `<span style="color:${labelColor};">${label}</span>`;
                            }

                            return `<span style="color:${labelColor};">${roundedValue}%</span>`;
                          }
                        }
                      }
                    ]
                  },
                  'xy'
                )}
              />
            ) : null}
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-3 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              Developer Profile Radar
            </h3>
            {developerRadarChart?.series?.length ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={{
                  chart: {
                    polar: true,
                    type: 'spline',
                    height: 560,
                    backgroundColor: 'transparent'
                  },
                  credits: { enabled: false },
                  title: { text: '' },
                  legend: {
                    ...chartLegendOptions,
                    align: 'center',
                    verticalAlign: 'top'
                  },
                  pane: {
                    startAngle: 0,
                    endAngle: 360
                  },
                  xAxis: {
                    ...xAxisTheme,
                    categories: developerRadarChart.categories,
                    tickmarkPlacement: 'on',
                    lineWidth: 0
                  },
                  yAxis: {
                    ...yAxisTheme,
                    gridLineInterpolation: 'polygon',
                    min: 0,
                    title: { text: null }
                  },
                  tooltip: {
                    shared: true,
                    useHTML: true,
                    formatter: lineTooltipFormatter
                  },
                  plotOptions: {
                    series: {
                      linecap: 'round',
                      connectEnds: true,
                      marker: { enabled: false }
                    }
                  },
                  series: developerRadarChart.series
                }}
              />
            ) : (
              <p
                className={`text-sm ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-500'
                }`}
              >
                There may not be enough developer profile data yet, or this
                analysis may not be included in your current plan.
              </p>
            )}
          </div>

          <div
            className={`rounded-sm border p-4 shadow-default ${
              isDarkMode
                ? 'border-strokedark bg-boxdark'
                : 'border-stroke bg-white'
            }`}
          >
            <h3
              className={`mb-3 text-base font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}
            >
              DevFeed Records
            </h3>
            {feedError && (
              <p className="mb-3 text-sm text-red-500">{feedError}</p>
            )}
            <div
              ref={feedListRef}
              className={`max-h-[760px] space-y-3 overflow-y-auto rounded-md border p-3 pr-2 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4'
                  : 'border-stroke bg-slate-50'
              }`}
            >
              {feedItems.map(item => {
                const stats = item?.stats || {};
                const hashtags = item?.hashtags || {};
                const commitSha = item?.commit_sha || '';
                const shortCommitSha = commitSha ? commitSha.slice(0, 6) : '';
                const commitUrl = item?.commit_url || '';
                const value =
                  averageDeveloperCost > 0
                    ? convertEffortToInvestment(
                        stats?.rev,
                        averageDeveloperCost
                      )
                    : typeof stats?.value === 'number'
                      ? Math.round(stats.value)
                      : 0;

                return (
                  <article
                    key={item?.id || `${item?.timestamp}-${item?.author?.name}`}
                    className={`rounded-md border p-4 shadow-sm transition hover:-translate-y-[1px] hover:shadow-md ${
                      isDarkMode
                        ? 'border-strokedark bg-boxdark'
                        : 'border-stroke bg-white'
                    }`}
                  >
                    <div
                      className={`flex flex-wrap items-center justify-between gap-2 text-sm ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      <div
                        className={`font-semibold ${
                          isDarkMode ? 'text-slate-200' : 'text-slate-700'
                        }`}
                      >
                        {item?.author?.name ||
                          selectedAuthor?.label ||
                          'Unknown Developer'}
                      </div>
                      <div>
                        {item?.timestamp
                          ? formatDistanceToNow(new Date(item.timestamp), {
                              addSuffix: true
                            })
                          : '-'}
                      </div>
                    </div>

                    <div className="mt-3 flex items-start justify-between gap-3">
                      <h4
                        className={`text-sm font-semibold ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}
                      >
                        {item?.impact_summary?.title || 'Untitled impact'}
                      </h4>
                      {shortCommitSha && commitUrl && (
                        <a
                          href={commitUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`shrink-0 rounded px-2 py-0.5 font-mono text-xs font-semibold transition ${
                            isDarkMode
                              ? 'bg-meta-4 text-slate-200 hover:bg-boxdark'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {shortCommitSha}
                        </a>
                      )}
                    </div>
                    <p
                      className={`mt-1 text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}
                    >
                      {item?.impact_summary?.detail || '-'}
                    </p>

                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
                      {hashtags?.project && (
                        <span
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-blue-500/15 text-blue-200'
                              : 'bg-blue-50 text-blue-700'
                          }`}
                        >
                          #{hashtags.project_name || hashtags.project}
                        </span>
                      )}
                      {hashtags?.repo && (
                        <span
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-fuchsia-500/15 text-fuchsia-200'
                              : 'bg-purple-50 text-purple-700'
                          }`}
                        >
                          #{hashtags.repo_name || hashtags.repo}
                        </span>
                      )}
                      {(hashtags?.categories || []).map(category => (
                        <span
                          key={`${item?.id || item?.timestamp}-${category}`}
                          className={`rounded-full px-3 py-1 ${
                            isDarkMode
                              ? 'bg-emerald-500/15 text-emerald-200'
                              : 'bg-emerald-50 text-emerald-700'
                          }`}
                        >
                          #{category}
                        </span>
                      ))}
                    </div>

                    <div
                      className={`mt-4 border-t border-dashed pt-3 text-sm ${
                        isDarkMode
                          ? 'border-strokedark text-slate-300'
                          : 'border-stroke text-slate-600'
                      }`}
                    >
                      <span className="font-semibold">REV:</span>{' '}
                      {stats?.rev || 0}
                      <span className="mx-2">|</span>
                      <span className="font-semibold">
                        +{stats?.insertions || 0}
                      </span>{' '}
                      / -{stats?.deletions || 0} lines
                      <span className="mx-2">|</span>
                      <span className="font-semibold">Value:</span> ${value}
                    </div>
                  </article>
                );
              })}

              {!feedItems.length && !feedLoading && !feedError && (
                <p
                  className={`text-sm ${
                    isDarkMode ? 'text-slate-400' : 'text-slate-500'
                  }`}
                >
                  No developer feed records in selected range.
                </p>
              )}

              {feedLoading && !feedItems.length && (
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${
                    isDarkMode
                      ? 'border-strokedark bg-meta-4 text-slate-300'
                      : 'border-stroke bg-slate-50 text-slate-500'
                  }`}
                >
                  <Loading type="bar" />
                </div>
              )}

              {feedHasMore && feedItems.length > 0 && (
                <div className="flex justify-center px-2 py-2">
                  <button
                    type="button"
                    onClick={() => {
                      logDeveloperReportInteraction(
                        'dev_feed_load_more_click',
                        {
                          has_cursor: Boolean(feedCursor),
                          has_buffered_items:
                            feedBufferedItemsRef.current.length > 0
                        }
                      );
                      loadFeed({ append: true, cursor: feedCursor });
                    }}
                    disabled={feedLoading}
                    className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                      isDarkMode
                        ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4'
                        : 'border-stroke bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {feedLoading ? <Loading type="inline" /> : 'Load more'}
                  </button>
                </div>
              )}

              {!feedHasMore && feedItems.length > 0 && (
                <div
                  className={`px-2 py-2 text-center text-xs ${
                    isDarkMode ? 'text-slate-500' : 'text-slate-400'
                  }`}
                >
                  You&apos;ve reached the end of the feed.
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <EntitySelectionModal
        isOpen={isDeveloperSelectionModalOpen}
        title="Select a developer"
        options={developerSearchOptions}
        selectedValue={selectedAuthorUid}
        selectedLabel={selectedAuthor?.label || ''}
        searchPlaceholder="Search developer..."
        emptyMessage="No developers found."
        confirmLabel="Pick a developer to open the report."
        isDarkMode={isDarkMode}
        canClose={Boolean(selectedAuthorUid)}
        onClose={() => setIsDeveloperSelectionModalOpen(false)}
        onSelect={handleDeveloperSearchSelect}
      />
    </div>
  );
};

export default DeveloperReport;
